import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsBuilding, BsPerson, BsSkipForwardFill, BsXCircleFill } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import ProgressBar from '#globalComponents/ProgressBar/ProgressBar';

const EMPTY_SUMMARY = {
	companiesCreated: 0,
	companiesUpdated: 0,
	contactsCreated: 0,
	contactsUpdated: 0,
	skipped: 0,
	failed: 0,
};

export default function ImportResultStep({ result, isLoading, totalRows, onClose }) {
	const [showErrors, setShowErrors] = useState(false);

	if (isLoading && (!result || result.status === 'processing')) {
		return <ProcessingView result={result} totalRows={totalRows} />;
	}

	const summary = { ...EMPTY_SUMMARY, ...(result?.summary ?? {}) };
	const errors = result?.errors ?? [];
	const totalCreated = summary.companiesCreated + summary.contactsCreated;
	const totalUpdated = summary.companiesUpdated + summary.contactsUpdated;

	return (
		<div>
			<div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
				<StatBadge
					icon={BsBuilding}
					label="Firmen"
					detail={`${summary.companiesCreated} neu · ${summary.companiesUpdated} akt.`}
					color="green"
				/>
				<StatBadge
					icon={BsPerson}
					label="Kontakte"
					detail={`${summary.contactsCreated} neu · ${summary.contactsUpdated} akt.`}
					color="blue"
				/>
				<StatBadge
					icon={BsSkipForwardFill}
					label="Übersprungen"
					detail={String(summary.skipped)}
					color="yellow"
				/>
				<StatBadge icon={BsXCircleFill} label="Fehlerhaft" detail={String(summary.failed)} color="red" />
			</div>

			{totalCreated + totalUpdated > 0 && (
				<div className="p-4 bg-success-200 border border-success-500/40 rounded-card mb-4">
					<p className="text-sm text-success-700 font-medium">
						Import abgeschlossen: {totalCreated} neu angelegt, {totalUpdated} aktualisiert.
					</p>
				</div>
			)}

			{(summary.dealsCreated > 0 || summary.dealsSkipped > 0) && (
				<div className="p-4 bg-primary-100 border border-primary-200 rounded-card mb-4">
					<p className="text-sm text-primary-700 font-medium">
						Pipeline-Zuweisung: {summary.dealsCreated ?? 0} Deal(s) angelegt
						{summary.dealsSkipped > 0 ? `, ${summary.dealsSkipped} übersprungen (bereits vorhanden)` : ''}.
					</p>
				</div>
			)}

			<ContactListResult summary={summary} />

			{totalCreated + totalUpdated === 0 && summary.failed === 0 && summary.skipped > 0 && (
				<div className="p-4 bg-caution-200 border border-caution-500/40 rounded-card mb-4">
					<p className="text-sm text-caution-700 font-medium">
						Es wurden keine Datensätze geändert (alle übersprungen).
					</p>
				</div>
			)}

			{errors.length > 0 && (
				<div className="mt-4">
					<button
						onClick={() => setShowErrors(!showErrors)}
						className="text-sm text-warning-700 hover:text-warning-700 font-medium"
					>
						{showErrors ? 'Fehler ausblenden' : `${errors.length} Fehler anzeigen`}
					</button>

					{showErrors && (
						<div className="mt-2 border border-warning-500/40 rounded-card max-h-[200px] overflow-y-auto">
							<table className="w-full text-sm">
								<thead className="bg-warning-200 sticky top-0">
									<tr>
										<th className="text-left px-3 py-2 font-medium text-warning-700">Zeile</th>
										<th className="text-left px-3 py-2 font-medium text-warning-700">Feld</th>
										<th className="text-left px-3 py-2 font-medium text-warning-700">Fehler</th>
									</tr>
								</thead>
								<tbody>
									{errors.map((err, i) => (
										<tr key={i} className="border-t border-warning-500/40">
											<td className="px-3 py-1.5 text-warning-700">{err.row}</td>
											<td className="px-3 py-1.5 text-warning-700">{err.field ?? '—'}</td>
											<td className="px-3 py-1.5 text-warning-700">{err.message}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			)}

			<div className="flex justify-end mt-6">
				<Button onClick={onClose}>Fertig</Button>
			</div>
		</div>
	);
}

function ContactListResult({ summary }) {
	if (summary.listFailed) {
		return (
			<div className="p-4 bg-warning-200 border border-warning-500/40 rounded-card mb-4">
				<p className="text-sm text-warning-700 font-medium">
					Die Daten wurden ins CRM importiert, konnten aber nicht in die Kontaktliste übernommen werden. Bitte
					fügen Sie sie über das CRM zur Liste hinzu.
				</p>
			</div>
		);
	}
	if (summary.listContactsAdded === undefined) return null;
	return (
		<div className="p-4 bg-primary-100 border border-primary-200 rounded-card mb-4">
			<p className="text-sm text-primary-700 font-medium">
				Kontaktliste: {summary.listContactsAdded} Kontakt(e) und {summary.listCompaniesAdded} Firma/Firmen neu
				aufgenommen
				{summary.listAlreadyPresent > 0 ? `, ${summary.listAlreadyPresent} bereits in der Liste` : ''}.
			</p>
		</div>
	);
}

function ProcessingView({ result, totalRows }) {
	const processed = result?.processedRows ?? 0;
	const percentage = totalRows > 0 ? Math.round((processed / totalRows) * 100) : 0;

	// After all rows are processed (100%), the backend still indexes, tags, fills the contact list and creates deals
	const isFinalizing = result?.phase === 'finalizing';

	return (
		<div className="flex flex-col items-center justify-center py-16">
			<div className="w-full max-w-md mb-4">
				<div className="flex justify-between text-sm text-text mb-1">
					<span>
						{processed} / {totalRows} Zeilen
					</span>
					<span>{percentage}%</span>
				</div>
				<ProgressBar value={processed} max={totalRows ?? 0} className="h-3" />
			</div>
			<p className="text-sm text-bgBase-800">
				{isFinalizing ? 'Import wird abgeschlossen...' : 'Import läuft...'}
			</p>
		</div>
	);
}

function StatBadge({ icon: Icon, label, detail, color }) {
	const colorMap = {
		green: 'bg-success-200 text-success-700 border-success-500/40',
		yellow: 'bg-caution-200 text-caution-700 border-caution-500/40',
		blue: 'bg-primary-100 text-primary-700 border-primary-200',
		red: 'bg-warning-200 text-warning-700 border-warning-500/40',
	};
	const iconColorMap = {
		green: 'text-success-500',
		yellow: 'text-caution-500',
		blue: 'text-primary-500',
		red: 'text-warning-500',
	};

	return (
		<div className={`flex flex-col items-center p-3 rounded-card border ${colorMap[color]}`}>
			<Icon className={`w-5 h-5 ${iconColorMap[color]} mb-1`} />
			<span className="text-sm font-bold">{detail}</span>
			<span className="text-xs">{label}</span>
		</div>
	);
}

ImportResultStep.propTypes = {
	result: PropTypes.object,
	isLoading: PropTypes.bool,
	totalRows: PropTypes.number,
	onClose: PropTypes.func.isRequired,
};

ProcessingView.propTypes = {
	result: PropTypes.object,
	totalRows: PropTypes.number,
};

StatBadge.propTypes = {
	icon: PropTypes.elementType.isRequired,
	label: PropTypes.string.isRequired,
	detail: PropTypes.string.isRequired,
	color: PropTypes.string.isRequired,
};

ContactListResult.propTypes = {
	summary: PropTypes.object.isRequired,
};
